import {WebSocketInstanceService} from './web.socket.instance.service';
import {Subject, Subscription} from 'rxjs';
import { EventBody, ResponseStatus, ResponseType, SubscribeResponseBody } from './web.socket.response.types';
import {RequestType} from './web.socket.request.types';
import {filter, take, takeWhile} from 'rxjs/operators';
import { WebSocketLoggerService } from './web.socket.logger.service';
import { IWebSocketSubscription } from './web.socket.instance.interface';

export class WebSocketSubscription implements IWebSocketSubscription {
  private subscriptionId = '';
  private eventReceivedSub: Subscription | undefined;
  public onEventReceived: Subject<EventBody> = new Subject<EventBody>();
  public onDestroy: Subject<string> = new Subject();
  private readonly logger = new WebSocketLoggerService();

  constructor(private readonly channel: string, private readonly instance: WebSocketInstanceService) {
    this.subscribe(); // start subscription
  }

  private subscribe(): void {
    this.subscriptionId = '';
    this.instance.send(RequestType.SUB, {channel: this.channel}).subscribe(messageId => {
      this.logger.log('WebSocketSubscription', 'request subscription for channel', messageId, this.channel);
      this.instance.onMessageReceived.pipe(
        takeWhile(() => this.subscriptionId === ''),
        take(1),
        filter(message => message?.header?.type === ResponseType.SUB_RESPONSE &&
          message?.header?.reqMessageId === messageId)
      ).subscribe(message => {
        if (message?.header.status === ResponseStatus.SUCCESS) {
          this.subscriptionId = (message?.body as SubscribeResponseBody).subscriptionId;
          this.startWatching();
        } else {
          this.logger.error('WebSocketSubscription', 'failed to create subscription, reason:', message?.header.errors?.[0]?.message ?? 'Unknown error');
        }
      });
    });
  }

  private startWatching(): void {
    this.logger.log('WebSocketSubscription', 'successfully subscribed, start watching...', this.subscriptionId);
    if (this.eventReceivedSub) {
      this.eventReceivedSub.unsubscribe();
    }
    this.eventReceivedSub = this.instance.onMessageReceived.pipe(
      filter(message => message?.header?.type === ResponseType.EVENT &&
              (message?.body as EventBody).subscriptionId === this.subscriptionId)
    ).subscribe(message => {
      this.onEventReceived.next(message?.body as EventBody);
    });
  }

  // this will stop onMessageReceived observer and drop all children observers
  private stopSubscription(): void {
    this.logger.log('WebSocketSubscription', 'successfully unsubscribed');
    this.eventReceivedSub?.unsubscribe();
    this.onDestroy.next(this.channel);
    this.onDestroy.complete();
    this.onEventReceived.complete();
  }

  // framework use only!
  public reconnect(): void {
    this.subscribe();
  }

  public destroy(): void {
    if (this.subscriptionId === '') {
      return this.stopSubscription();
    }
    this.instance.send(RequestType.UNSUB, {subscriptionId: this.subscriptionId}).subscribe(messageId => {
      this.instance.onMessageReceived.pipe(
        take(1),
        filter(message => message?.header?.reqMessageId === messageId)
      ).subscribe(() => this.stopSubscription());
    });
  }
}
